/* ============================================================================
 * GFI Nexus — Supabase CRUD helpers with in-memory cache.
 *
 * Parallel to the localStorage helpers used today (loadLS / saveLS) but
 * goes to Supabase as source of truth. Designed so paso 3b can swap a
 * registry from localStorage to Supabase with minimal code churn:
 *
 *   // before (localStorage):
 *   var bills = loadLS('gfi-bills', []);
 *   saveLS('gfi-bills', updatedBills);
 *
 *   // after (Supabase):
 *   var bills = await loadFromSupabase('bills');
 *   await saveToSupabase('bills', billRecord);
 *   await deleteFromSupabase('bills', billRecord.id);
 *
 * The Supabase row uses `legacy_id` as the upsert conflict key, so the
 * app keeps treating the original string ID ("BILL-0042", a Date.now()
 * number) as `record.id`. The real uuid is exposed as `record._uuid`
 * for new linking when needed.
 *
 * Cache strategy
 * --------------
 *   load → checks in-memory cache; if fresh (< TTL), returns it.
 *          otherwise SELECT *, maps rows to app-shape records, caches,
 *          notifies subscribers.
 *   save → upserts the row, refreshes the cache entry locally
 *          (mutate + bump fetchedAt), notifies subscribers. NO refetch.
 *   del  → DELETE on Supabase, drops from cache, notifies subscribers.
 *
 * Subscriptions
 * -------------
 *   onChange(table, cb) returns an unsubscribe. Modules can subscribe
 *   to be re-rendered when their table mutates (from save/delete from
 *   anywhere in the app, or a cache refresh).
 *
 * What's NOT here
 * ---------------
 *   • Module wiring — paso 3b connects this to BillRegistry / etc.
 *   • Realtime subscriptions to Supabase changes — could be added on
 *     top of `onChange` later.
 *   • Pagination / partial loads — every load fetches the full table.
 *     Fine for current data volume (<10k rows per table); revisit
 *     if/when a season's bills hit five-figure counts.
 *   • Document upload — file blobs still go to a separate Storage
 *     bucket once paso 4 lands.
 * ============================================================================
 */

import { supabase } from './supabaseClient';

/* ─── Cache + subscriptions ─────────────────────────────────────────────── */
/* Module-level Map keyed by table name. Survives across React re-renders
   but not page reloads (which is the desired semantics — a fresh page
   load should hit Supabase to pick up other-user mutations). */
const cache = new Map();   // table → { records, fetchedAt }
const subs  = new Map();   // table → Set<callback>

const DEFAULT_TTL_MS = 60 * 1000;

function notify(table) {
  const set = subs.get(table);
  if (!set || set.size === 0) return;
  const records = (cache.get(table) || { records: [] }).records;
  set.forEach(function (cb) { try { cb(records); } catch (e) { /* swallow */ } });
}

/* ─── Per-table configuration ───────────────────────────────────────────── */
/* `columns`: typed-column promotions (everything else lands in `extra`).
   Each entry: { db, app } — db is the snake_case Postgres column, app
   is the camelCase field the React code uses on the record.
   `fks`: foreign keys. Each entry: { app, table } — `app` is the
   record's reference field (carries the legacy string id), `table` is
   the cache key to resolve the uuid against. The DB columns are derived
   from `app` (e.g. supplierId → supplier_legacy_id + supplier_id).
   `pk` (optional): conflict column for upsert. Defaults to 'legacy_id'.
   Shipper_settings uses 'supplier_id', company_profile uses 'id'.

   Anything in the record but NOT in `columns`/`fks` is preserved as-is
   inside the JSONB `extra` column — guarantees no field gets dropped
   even when the schema doesn't have an explicit slot for it. */
const TABLE_CONFIG = {
  audit_comments: {
    pk: 'id',
    columns: [
      { db: 'audit_id',     app: 'auditId' },
      { db: 'target_type',  app: 'targetType' },
      { db: 'target_id',    app: 'targetId' },
      { db: 'parent_id',    app: 'parentId' },
      { db: 'author_name',  app: 'authorName' },
      { db: 'author_role',  app: 'authorRole' },
      { db: 'body',         app: 'body' },
      { db: 'created_at',   app: 'createdAt' },
    ],
    fks: [],
  },

  seasons: {
    columns: [
      { db: 'season_name',  app: 'seasonName' },
      { db: 'start_date',   app: 'startDate' },
      { db: 'end_estimate', app: 'endEstimate' },
      { db: 'profit_goal',  app: 'profitGoal' },
      { db: 'status',       app: 'status' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  commodities: {
    columns: [
      { db: 'name_en',   app: 'nameEn' },
      { db: 'name_es',   app: 'nameEs' },
      { db: 'hts_code',  app: 'htsCode' },
      { db: 'tax_code',  app: 'taxCode' },
      { db: 'varieties', app: 'varieties' },
    ],
    fks: [],
  },

  suppliers: {
    columns: [
      { db: 'supplier_type',   app: 'supplierType' },
      { db: 'initials',        app: 'initials' },
      { db: 'short_name',      app: 'shortName' },
      { db: 'full_name',       app: 'fullName' },
      { db: 'contact',         app: 'contact' },
      { db: 'phone',           app: 'phone' },
      { db: 'email',           app: 'email' },
      { db: 'address',         app: 'address' },
      { db: 'colony',          app: 'colony' },
      { db: 'city',            app: 'city' },
      { db: 'state',           app: 'state' },
      { db: 'country',         app: 'country' },
      { db: 'zip',             app: 'zip' },
      { db: 'payment_term',    app: 'paymentTerm' },
      { db: 'commission_rate', app: 'commissionRate' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [],
  },

  clients: {
    columns: [
      { db: 'initials',        app: 'initials' },
      { db: 'short_name',      app: 'shortName' },
      { db: 'full_name',       app: 'fullName' },
      { db: 'company',         app: 'company' },
      { db: 'dba',             app: 'dba' },
      { db: 'tax_id',          app: 'taxId' },
      { db: 'contact',         app: 'contact' },
      { db: 'phone',           app: 'phone' },
      { db: 'email',           app: 'email' },
      { db: 'address',         app: 'address' },
      { db: 'city',            app: 'city' },
      { db: 'state',           app: 'state' },
      { db: 'zip',             app: 'zip' },
      { db: 'country',         app: 'country' },
      { db: 'payment_term',    app: 'paymentTerm' },
      { db: 'commission_rate', app: 'commissionRate' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [],
  },

  products: {
    columns: [
      { db: 'product_code',       app: 'productCode' },
      { db: 'sku',                app: 'sku' },
      { db: 'product',            app: 'product' },
      { db: 'pack_type',          app: 'packType' },
      { db: 'size',               app: 'size' },
      { db: 'units_per_case',     app: 'unitsPerCase' },
      { db: 'cases_per_pallet',   app: 'casesPerPallet' },
      { db: 'label',              app: 'label' },
      { db: 'label_initials',     app: 'labelInitials' },
      { db: 'category',           app: 'category' },
      { db: 'description',        app: 'description' },
      { db: 'weight_per_case_lb', app: 'weightPerCaseLb' },
      { db: 'weight_per_case_kg', app: 'weightPerCaseKg' },
      { db: 'is_active',          app: 'isActive' },
      { db: 'documents',          app: 'documents' },
    ],
    fks: [],
  },

  warehouses: {
    columns: [
      { db: 'initials',     app: 'initials' },
      { db: 'short_name',   app: 'shortName' },
      { db: 'full_name',    app: 'fullName' },
      { db: 'address',      app: 'address' },
      { db: 'city',         app: 'city' },
      { db: 'state',        app: 'state' },
      { db: 'zip',          app: 'zip' },
      { db: 'country',      app: 'country' },
      { db: 'phone',        app: 'phone' },
      { db: 'email',        app: 'email' },
      { db: 'payment_term', app: 'paymentTerm' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  brokers: {
    columns: [
      { db: 'short_name',   app: 'shortName' },
      { db: 'full_name',    app: 'fullName' },
      { db: 'initials',     app: 'initials' },
      { db: 'contact',      app: 'contact' },
      { db: 'phone',        app: 'phone' },
      { db: 'email',        app: 'email' },
      { db: 'addresses',    app: 'addresses' },
      { db: 'payment_term', app: 'paymentTerm' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  quality_control: {
    columns: [
      { db: 'initials',     app: 'initials' },
      { db: 'short_name',   app: 'shortName' },
      { db: 'full_name',    app: 'fullName' },
      { db: 'address',      app: 'address' },
      { db: 'city',         app: 'city' },
      { db: 'state',        app: 'state' },
      { db: 'zip',          app: 'zip' },
      { db: 'country',      app: 'country' },
      { db: 'phone',        app: 'phone' },
      { db: 'email',        app: 'email' },
      { db: 'payment_term', app: 'paymentTerm' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  certifiers: {
    columns: [
      { db: 'initials',     app: 'initials' },
      { db: 'short_name',   app: 'shortName' },
      { db: 'full_name',    app: 'fullName' },
      { db: 'address',      app: 'address' },
      { db: 'city',         app: 'city' },
      { db: 'state',        app: 'state' },
      { db: 'zip',          app: 'zip' },
      { db: 'country',      app: 'country' },
      { db: 'phone',        app: 'phone' },
      { db: 'email',        app: 'email' },
      { db: 'payment_term', app: 'paymentTerm' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  growers: {
    columns: [
      { db: 'short_name',      app: 'shortName' },
      { db: 'full_name',       app: 'fullName' },
      { db: 'contact',         app: 'contact' },
      { db: 'phone',           app: 'phone' },
      { db: 'email',           app: 'email' },
      { db: 'location',        app: 'location' },
      { db: 'production_type', app: 'productionType' },
      { db: 'certifications',  app: 'certifications' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [
      { app: 'supplierId', table: 'suppliers' },
    ],
  },

  certifications: {
    columns: [
      { db: 'cert_number',  app: 'certNumber' },
      { db: 'cert_type',    app: 'certType' },
      { db: 'certifier',    app: 'certifier' },
      { db: 'issued_date',  app: 'issuedDate' },
      { db: 'expires_date', app: 'expiresDate' },
      { db: 'status',       app: 'status' },
      { db: 'document',     app: 'document' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [],
  },

  export_certs: {
    columns: [
      { db: 'cert_number',     app: 'certNumber' },
      { db: 'shipper_name',    app: 'shipperName' },
      { db: 'product_name',    app: 'productName' },
      { db: 'certifier',       app: 'certifier' },
      { db: 'period_from',     app: 'periodFrom' },
      { db: 'period_to',       app: 'periodTo' },
      { db: 'total_invoices',  app: 'totalInvoices' },
      { db: 'used_invoices',   app: 'usedInvoices' },
      { db: 'linked_lots',     app: 'linkedLots' },
      { db: 'document',        app: 'document' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [
      { app: 'supplierId', table: 'suppliers' },
    ],
  },

  fee_catalog: {
    columns: [
      { db: 'name',                 app: 'name' },
      { db: 'default_rate',         app: 'defaultRate' },
      { db: 'default_qty',          app: 'defaultQty' },
      { db: 'default_passthrough',  app: 'defaultPassthrough' },
      { db: 'is_customs_fee',       app: 'isCustomsFee' },
      { db: 'notes',                app: 'notes' },
    ],
    fks: [],
  },

  fee_categories: {
    columns: [
      { db: 'name', app: 'name' },
    ],
    fks: [],
  },

  shipments: {
    columns: [
      { db: 'lot',              app: 'lot' },
      { db: 'shipper_name',     app: 'shipperName' },
      { db: 'shipper_source',   app: 'shipperSource' },
      { db: 'date_departure',   app: 'dateDeparture' },
      { db: 'date_arrival',     app: 'dateArrival' },
      { db: 'week',             app: 'week' },
      { db: 'proforma',         app: 'proforma' },
      { db: 'shipper_invoice',  app: 'shipperInvoice' },
      { db: 'entry_number',     app: 'entryNumber' },
      { db: 'alpha_code',       app: 'alphaCode' },
      { db: 'num_economico',    app: 'numEconomico' },
      { db: 'warehouse_name',   app: 'warehouseName' },
      { db: 'status',           app: 'status' },
      { db: 'lines',            app: 'lines' },
      { db: 'pallet_manifest',  app: 'palletManifest' },
      { db: 'transport_data',   app: 'transportData' },
      { db: 'documents',        app: 'documents' },
      { db: 'notes',            app: 'notes' },
    ],
    fks: [
      { app: 'seasonId',    table: 'seasons' },
      { app: 'supplierId',  table: 'suppliers' },
      { app: 'warehouseId', table: 'warehouses' },
      { app: 'brokerId',    table: 'brokers' },
    ],
  },

  shipper_shipments: {
    columns: [
      { db: 'consec_embarque', app: 'consecEmbarque' },
      { db: 'shipper_invoice', app: 'shipperInvoice' },
      { db: 'proformas',       app: 'proformas' },
      { db: 'docs',            app: 'docs' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [
      { app: 'supplierId', table: 'suppliers' },
    ],
  },

  bills: {
    columns: [
      { db: 'lot',                app: 'lot' },
      { db: 'shipper_name',       app: 'shipperName' },
      { db: 'bill_date',          app: 'billDate' },
      { db: 'due_date',           app: 'dueDate' },
      { db: 'proforma',           app: 'proforma' },
      { db: 'shipper_invoice',    app: 'shipperInvoice' },
      { db: 'entry_number',       app: 'entryNumber' },
      { db: 'reference',          app: 'reference' },
      { db: 'commission_rate',    app: 'commissionRate' },
      { db: 'status',             app: 'status' },
      { db: 'amount_paid',        app: 'amountPaid' },
      { db: 'amount_due',         app: 'amountDue' },
      { db: 'lines',              app: 'lines' },
      { db: 'fees',               app: 'fees' },
      { db: 'credits',            app: 'credits' },
      { db: 'balance_transfers',  app: 'balanceTransfers' },
      { db: 'payments',           app: 'payments' },
      { db: 'export_certs',       app: 'exportCerts' },
      { db: 'documents',          app: 'documents' },
      { db: 'notes',              app: 'notes' },
      { db: 'pallet_detail',      app: 'palletDetail' },
    ],
    fks: [
      { app: 'shipmentId', table: 'shipments' },
      { app: 'seasonId',   table: 'seasons' },
      { app: 'supplierId', table: 'suppliers' },
    ],
  },

  invoices: {
    columns: [
      { db: 'pu_number',          app: 'puNumber' },
      { db: 'invoice_date',       app: 'date' },
      { db: 'due_date',           app: 'dueDate' },
      { db: 'client_name',        app: 'clientName' },
      { db: 'client_initials',    app: 'clientInitials' },
      { db: 'client_ref',         app: 'clientRef' },
      { db: 'lot',                app: 'lot' },
      { db: 'shipper_name',       app: 'shipperName' },
      { db: 'commission_rate',    app: 'commissionRate' },
      { db: 'status',             app: 'status' },
      { db: 'amount_paid',        app: 'amountPaid' },
      { db: 'lines',              app: 'lines' },
      { db: 'fees',               app: 'fees' },
      { db: 'credits',            app: 'credits' },
      { db: 'balance_transfers',  app: 'balanceTransfers' },
      { db: 'payments',           app: 'payments' },
      { db: 'linked_lots',        app: 'linkedLots' },
      { db: 'linked_po_line_id',  app: 'linkedPoLineId' },
      { db: 'remarks',            app: 'remarks' },
      { db: 'documents',          app: 'documents' },
      { db: 'pallet_allocations', app: 'palletAllocations' },
    ],
    fks: [
      { app: 'seasonId',   table: 'seasons' },
      { app: 'clientId',   table: 'clients' },
      { app: 'shipmentId', table: 'shipments' },
      { app: 'billId',     table: 'bills' },
    ],
  },

  payments: {
    columns: [
      { db: 'payment_type',   app: 'type' },
      { db: 'payment_date',   app: 'paymentDate' },
      { db: 'amount',         app: 'amount' },
      { db: 'payment_method', app: 'paymentMethod' },
      { db: 'reference',      app: 'reference' },
      { db: 'client_name',    app: 'clientName' },
      { db: 'shipper_name',   app: 'shipperName' },
      { db: 'allocations',    app: 'allocations' },
      { db: 'notes',          app: 'notes' },
      { db: 'concept',        app: 'concept' },
      { db: 'source',         app: 'source' },
      { db: 'documents',      app: 'documents' },
    ],
    fks: [
      { app: 'clientId',   table: 'clients' },
      { app: 'supplierId', table: 'suppliers' },
    ],
  },

  expense_bills: {
    columns: [
      { db: 'vendor',            app: 'vendor' },
      { db: 'reference',         app: 'reference' },
      { db: 'expense_date',      app: 'date' },
      { db: 'due_date',          app: 'dueDate' },
      { db: 'amount',            app: 'amount' },
      { db: 'amount_paid',       app: 'amountPaid' },
      { db: 'status',            app: 'status' },
      { db: 'payment_method',    app: 'paymentMethod' },
      { db: 'payment_reference', app: 'paymentReference' },
      { db: 'fees',              app: 'fees' },
      { db: 'distribution',      app: 'distribution' },
      { db: 'documents',         app: 'documents' },
      { db: 'notes',             app: 'notes' },
    ],
    fks: [
      /* `vendorId` in localStorage points at a row in `suppliers`
         (we don't have a separate vendors table — expense vendors live
         in the suppliers catalog with supplierType='vendor'). */
      { app: 'vendorId', table: 'suppliers' },
    ],
  },

  fee_transfers: {
    /* 2026-10-16 — wired up to the live app for the first time (see
       migrations/2026-10-16_fee_transfers_live_wiring.sql). sourceLot/
       targetPU are plain text (used by the Bill→Invoice path), and
       sourceExpenseBill is the fk for the newer Expense Bill→Bill path.
       fk app names below match the exact field names BillRegistry.js/
       ExpenseBillRegistry.js actually set on a transfer record — not the
       shortened names (sourceBill/sourceInvoice) the original unused
       stub had. */
    columns: [
      { db: 'source_type',  app: 'sourceType' },
      { db: 'source_pu',    app: 'sourcePU' },
      { db: 'source_lot',   app: 'sourceLot' },
      { db: 'target_type',  app: 'targetType' },
      { db: 'target_lot',   app: 'targetLot' },
      { db: 'target_pu',    app: 'targetPU' },
      { db: 'fee_type',     app: 'feeType' },
      { db: 'fee_custom',   app: 'feeCustom' },
      { db: 'qty',          app: 'qty' },
      { db: 'rate',         app: 'rate' },
      { db: 'amount',       app: 'amount' },
      { db: 'pct_rate',     app: 'pctRate' },
      { db: 'status',       app: 'status' },
      { db: 'date_created', app: 'dateCreated' },
      { db: 'notes',        app: 'notes' },
    ],
    fks: [
      { app: 'sourceBillId',        table: 'bills' },
      { app: 'sourceExpenseBill',   table: 'expense_bills' },
      { app: 'targetBillId',        table: 'bills' },
      { app: 'targetInvoiceId',     table: 'invoices' },
    ],
  },

  fixed_pos: {
    columns: [
      { db: 'po_number',           app: 'poNumber' },
      { db: 'pu_number',           app: 'puNumber' },
      { db: 'po_date',             app: 'date' },
      { db: 'week',                app: 'week' },
      { db: 'delivery_date',       app: 'deliveryDate' },
      { db: 'ship_date',           app: 'shipDate' },
      { db: 'client_name',         app: 'clientName' },
      { db: 'product_base',        app: 'productBase' },
      { db: 'product_name',        app: 'productName' },
      { db: 'product_description', app: 'productDescription' },
      { db: 'category',            app: 'category' },
      { db: 'pallets_sent',        app: 'palletsSent' },
      { db: 'cases_sent',          app: 'casesSent' },
      { db: 'logger_number',       app: 'loggerNumber' },
      { db: 'sale_price',          app: 'salePrice' },
      { db: 'reported_price',      app: 'reportedPrice' },
      { db: 'commission_rate',     app: 'commissionRate' },
      { db: 'sale_status',         app: 'saleStatus' },
      { db: 'notes',               app: 'notes' },
    ],
    fks: [
      { app: 'clientId',         table: 'clients' },
      { app: 'productId',        table: 'products' },
      { app: 'linkedInvoiceId',  table: 'invoices' },
    ],
  },

  consignment_pos: {
    columns: [
      { db: 'po_number',             app: 'poNumber' },
      { db: 'pu_number',             app: 'puNumber' },
      { db: 'po_date',               app: 'date' },
      { db: 'week',                  app: 'week' },
      { db: 'delivery_date',         app: 'deliveryDate' },
      { db: 'ship_date',             app: 'shipDate' },
      { db: 'client_name',           app: 'clientName' },
      { db: 'product_base',          app: 'productBase' },
      { db: 'product_name',          app: 'productName' },
      { db: 'product_description',   app: 'productDescription' },
      { db: 'category',              app: 'category' },
      { db: 'pallets_sent',          app: 'palletsSent' },
      { db: 'cases_sent',            app: 'casesSent' },
      { db: 'logger_number',         app: 'loggerNumber' },
      { db: 'price_low',             app: 'priceLow' },
      { db: 'price_high',            app: 'priceHigh' },
      { db: 'price_midpoint',        app: 'priceMidpoint' },
      { db: 'sale_price',            app: 'salePrice' },
      { db: 'reported_low',          app: 'reportedLow' },
      { db: 'reported_high',         app: 'reportedHigh' },
      { db: 'reported_price',        app: 'reportedPrice' },
      { db: 'client_commission_rate', app: 'clientCommissionRate' },
      { db: 'gfi_commission_rate',   app: 'gfiCommissionRate' },
      { db: 'sale_status',           app: 'saleStatus' },
      { db: 'notes',                 app: 'notes' },
    ],
    fks: [
      { app: 'clientId',         table: 'clients' },
      { app: 'productId',        table: 'products' },
      { app: 'linkedInvoiceId',  table: 'invoices' },
    ],
  },

  /* shipper_settings has supplier_id as PK (one row per supplier). The
     load path returns records keyed by supplierLegacyId in the app
     shape; the save path takes a record whose `id` is the supplier
     legacy id. */
  shipper_settings: {
    pk: 'supplier_id',
    pkResolveTable: 'suppliers', /* record.id is a supplier's legacy id */
    columns: [
      { db: 'invoice_prefix',       app: 'invoicePrefix' },
      { db: 'proforma_prefix',      app: 'proformaPrefix' },
      { db: 'next_proforma_number', app: 'nextProformaNumber' },
      { db: 'embarque_prefix',      app: 'embarquePrefix' },
      { db: 'next_embarque_number', app: 'nextEmbarqueNumber' },
      { db: 'authorized_products',  app: 'authorizedProducts' },
      { db: 'assigned_brokers',     app: 'assignedBrokers' },
      /* FIX 50 — admin-assigned warehouses (jsonb array of warehouse
         legacy ids). When present the shipper's warehouse dropdown
         filters down to these; empty/null means no restriction. */
      { db: 'assigned_warehouses',  app: 'assignedWarehouses' },
    ],
    fks: [
      /* `defaultUSBroker` in the app maps to default_us_broker_id (uuid).
         No legacy_id sibling — the schema only stores the uuid. */
      { app: 'defaultUSBroker', table: 'brokers', dbUuid: 'default_us_broker_id', noLegacyColumn: true },
    ],
  },

  /* company_profile is a singleton row with PK 'gfi' (text). The
     record's app-side `id` is 'gfi' for consistency. Column mapping
     matches what CompanyProfile.js actually puts on the record
     (legalName / organicCert / etc.) — fields the app uses that
     don't map to a typed column (dba, taxId) land in `extra` and
     come back on load via the spread. */
  /* organic_audits — Arturo's per-audit folder. `reports` + `other_docs`
     hold structured arrays (metadata + Storage paths); the heavy
     binaries themselves stay in IndexedDB (legacy) or in the
     `audit-docs` Supabase Storage bucket once each report flows
     through saveDocBlob. */
  organic_audits: {
    columns: [
      { db: 'name',         app: 'name' },
      { db: 'year',         app: 'year' },
      { db: 'start_date',   app: 'startDate' },
      { db: 'end_date',     app: 'endDate' },
      { db: 'auditor_name', app: 'auditorName' },
      { db: 'status',       app: 'status' },
      { db: 'reports',      app: 'reports' },
      { db: 'other_docs',   app: 'otherDocs' },
    ],
    fks: [],
  },

  company_profile: {
    pk: 'id',
    singletonId: 'gfi',
    columns: [
      { db: 'company_name',         app: 'legalName' },
      { db: 'address_line',         app: 'addressLine' },
      { db: 'city',                 app: 'city' },
      { db: 'state',                app: 'state' },
      { db: 'zip',                  app: 'zip' },
      { db: 'country',              app: 'country' },
      { db: 'phone',                app: 'phone' },
      { db: 'email',                app: 'email' },
      { db: 'website',              app: 'website' },
      { db: 'organic_cert_number',  app: 'organicCert' },
      { db: 'organic_certifier',    app: 'organicCertifier' },
      { db: 'branding',             app: 'branding' },
      { db: 'notes',                app: 'notes' },
    ],
    fks: [],
  },

  /* SweetLeaf Produce — a parallel invoice ledger for the SL brand
     that GFI sells to Four Seasons under. The records mirror the
     `invoices` shape in spirit (lines, fees, credits, payments) but
     use their own consecutive (INV-XXXXX) tracked in
     sweetleaf_settings. Everything else (FKs, status, dates) is
     handled inside the SweetLeafPortal module via extra. */
  sweetleaf_invoices: {
    columns: [],
    fks: [],
  },

  /* Singleton settings row for SweetLeaf (PK = 'sl'). Tracks the
     next invoice consecutive, the configurable prefix, and the
     dedicated Gmail account + HTML signature used for SL sends. */
  sweetleaf_settings: {
    pk: 'id',
    singletonId: 'sl',
    columns: [
      { db: 'next_invoice_number', app: 'nextInvoiceNumber' },
      { db: 'invoice_prefix',      app: 'invoicePrefix' },
      { db: 'email_account',       app: 'emailAccount' },
      { db: 'email_signature',     app: 'emailSignature' },
      /* Promoted from the `extra` catch-all — this table apparently has
         no `extra` jsonb column (it isn't tracked in supabaseSchema.sql
         at all), so anything not explicitly mapped here was silently
         lost on the next Supabase sync. Real bug: passingRecipients
         (Four Seasons Passing/BOL contacts) kept reverting to empty.
         Mapping these 6 explicitly removes the dependency on `extra`
         for this table. Matching ALTER TABLE run manually in Supabase. */
      { db: 'passing_recipients',         app: 'passingRecipients' },
      { db: 'factoring_recipients',       app: 'factoringRecipients' },
      { db: 'factoring_email_subject',    app: 'factoringEmailSubject' },
      { db: 'factoring_email_template',   app: 'factoringEmailTemplate' },
      { db: 'confidentiality_disclosure', app: 'confidentialityDisclosure' },
      { db: 'masked_email_account',       app: 'maskedEmailAccount' },
      /* Automated Protection Status Reminder (2026-09-23) — see that
         migration's comment for what these control. */
      { db: 'protection_reminder_interval_days', app: 'protectionReminderIntervalDays' },
      { db: 'protection_reminder_max_auto',      app: 'protectionReminderMaxAuto' },
      /* 2026-09-29 (Arturo: "para las alertas de porteccion de
         sweetleaf necesito otra cuenta") — dedicated, name-free From
         alias for ONLY the automated daily sweep
         (protection-reminder-send.js); every manual SweetLeaf send
         keeps using maskedEmailAccount above unchanged. Falls back to
         maskedEmailAccount when blank (see that function). */
      { db: 'protection_reminder_masked_email_account', app: 'protectionReminderMaskedEmailAccount' },
      /* 2026-10-06 — Past Due Notice recipients (Settings → SweetLeaf). */
      { db: 'past_due_recipients', app: 'pastDueRecipients' },
    ],
    fks: [],
  },

  /* Forecast & Market Intelligence — external API config (fix-dashboard.md
     Fase B). One row per API; record.id is the api_name slug (e.g.
     'usda_market_news'), stored as the usual legacy_id — no singleton,
     no special pk. api_key is base64-"obfuscated" client-side before it
     ever reaches this mapper (see ApiSettingsRegistry.js) — this table
     has no RLS, same as the rest of this schema, so treat it as visible
     to any signed-in session, not as a secret store. */
  api_settings: {
    columns: [
      { db: 'api_name',     app: 'apiName' },
      { db: 'api_key',      app: 'apiKey' },
      { db: 'endpoint_url', app: 'endpointUrl' },
      { db: 'enabled',      app: 'enabled' },
      { db: 'last_sync',    app: 'lastSync' },
      { db: 'status',       app: 'status' },
    ],
    fks: [],
  },

  /* USDA Market News (FOB Prices module) — separate USDA sub-API/key from
     the Organic INTEGRITY sync (usda_operations, not routed through this
     mapper at all; the Netlify usda-sync* functions write it via raw
     REST fetch). legacy_id defaults per the usual conflict-key rule
     (cfg.pk || 'legacy_id') since these rows aren't migrated from any
     existing gfi-* localStorage key — the sync function will construct
     one from source_report_id + product/category/size. */
  usda_market_prices: {
    columns: [
      { db: 'report_date',       app: 'reportDate' },
      { db: 'week_iso',          app: 'weekIso' },
      { db: 'year',              app: 'year' },
      { db: 'product',           app: 'product' },
      { db: 'category',          app: 'category' },
      { db: 'size',              app: 'size' },
      { db: 'market_location',   app: 'marketLocation' },
      { db: 'price_low',         app: 'priceLow' },
      { db: 'price_high',        app: 'priceHigh' },
      { db: 'movement',          app: 'movement' },
      { db: 'trend',             app: 'trend' },
      { db: 'remarks',           app: 'remarks' },
      { db: 'volume_original',   app: 'volumeOriginal' },
      { db: 'volume_unit',       app: 'volumeUnit' },
      { db: 'trucks_equivalent', app: 'trucksEquivalent' },
      { db: 'source_report_id',  app: 'sourceReportId' },
      { db: 'raw_json',          app: 'rawJson' },
    ],
    fks: [],
  },

  /* report_slug (not legacy_id) is the natural conflict key here — the
     Netlify sync function upserts by which USDA report it's tracking,
     not a migrated localStorage id. */
  usda_prices_sync_state: {
    pk: 'report_slug',
    columns: [
      { db: 'report_slug',       app: 'reportSlug' },
      { db: 'last_synced_at',    app: 'lastSyncedAt' },
      { db: 'last_status',       app: 'lastStatus' },
      { db: 'error_message',     app: 'errorMessage' },
      { db: 'records_last_sync', app: 'recordsLastSync' },
    ],
    fks: [],
  },

  historical_prices: {
    columns: [
      { db: 'source',         app: 'source' },
      { db: 'report_source',  app: 'reportSource' },
      { db: 'market_city',    app: 'marketCity' },
      { db: 'market_country', app: 'marketCountry' },
      { db: 'product',        app: 'product' },
      { db: 'variety',        app: 'variety' },
      { db: 'category',       app: 'category' },
      { db: 'origin',         app: 'origin' },
      { db: 'price_low',      app: 'priceLow' },
      { db: 'price_high',     app: 'priceHigh' },
      { db: 'price_mostly',   app: 'priceMostly' },
      { db: 'unit',           app: 'unit' },
      { db: 'package_size',   app: 'packageSize' },
      { db: 'report_date',    app: 'reportDate' },
      { db: 'captured_at',    app: 'capturedAt' },
    ],
    fks: [],
  },

  ai_analyst_log: {
    columns: [
      { db: 'log_date',       app: 'logDate' },
      { db: 'product',        app: 'product' },
      { db: 'category',       app: 'category' },
      { db: 'base_forecast',  app: 'baseForecast' },
      { db: 'adjusted_price', app: 'adjustedPrice' },
      { db: 'adjustment_pct', app: 'adjustmentPct' },
      { db: 'confidence',     app: 'confidence' },
      { db: 'reasoning',      app: 'reasoning' },
      { db: 'plain_explanation', app: 'plainExplanation' },
      { db: 'context',        app: 'context' },
      { db: 'status',         app: 'status' },
      { db: 'reviewed_at',    app: 'reviewedAt' },
    ],
    fks: [],
  },

  manual_price_quotes: {
    columns: [
      { db: 'product',     app: 'product' },
      { db: 'category',    app: 'category' },
      { db: 'price',       app: 'price' },
      { db: 'quoted_date', app: 'quotedDate' },
      { db: 'notes',       app: 'notes' },
    ],
    fks: [],
  },

  mexico_export_market_quotes: {
    columns: [
      { db: 'product',     app: 'product' },
      { db: 'category',    app: 'category' },
      { db: 'price',       app: 'price' },
      { db: 'quoted_date', app: 'quotedDate' },
      { db: 'notes',       app: 'notes' },
    ],
    fks: [],
  },

  macro_indicators: {
    columns: [
      { db: 'metric',       app: 'metric' },
      { db: 'value',        app: 'value' },
      { db: 'unit',         app: 'unit' },
      { db: 'report_date',  app: 'reportDate' },
    ],
    fks: [],
  },

  truck_crossings: {
    columns: [
      { db: 'product',         app: 'product' },
      { db: 'district',        app: 'district' },
      { db: 'origin',          app: 'origin' },
      { db: 'organic',         app: 'organic' },
      { db: 'transportation',  app: 'transportation' },
      { db: 'lb_units',        app: 'lbUnits' },
      { db: 'package_count',   app: 'packageCount' },
      { db: 'shipment_date',   app: 'shipmentDate' },
      { db: 'season',          app: 'season' },
      { db: 'captured_at',     app: 'capturedAt' },
    ],
    fks: [],
  },

  internal_price_history: {
    columns: [
      { db: 'report_date',     app: 'reportDate' },
      { db: 'product',         app: 'product' },
      { db: 'category',        app: 'category' },
      { db: 'size',            app: 'size' },
      { db: 'client_price',    app: 'clientPrice' },
      { db: 'supplier_price',  app: 'supplierPrice' },
      { db: 'client_name',     app: 'clientName' },
      { db: 'supplier_name',   app: 'supplierName' },
      { db: 'notes',           app: 'notes' },
    ],
    fks: [],
  },

  historical_prices_sync_state: {
    pk: 'source',
    columns: [
      { db: 'source',             app: 'source' },
      { db: 'last_run_at',        app: 'lastRunAt' },
      { db: 'last_status',        app: 'lastStatus' },
      { db: 'reports_processed',  app: 'reportsProcessed' },
      { db: 'rows_ingested',      app: 'rowsIngested' },
      { db: 'duplicates_updated', app: 'duplicatesUpdated' },
      { db: 'error_message',      app: 'errorMessage' },
      { db: 'duration_ms',        app: 'durationMs' },
    ],
    fks: [],
  },
};

function configFor(table) {
  const c = TABLE_CONFIG[table];
  if (!c) throw new Error('Unknown table: ' + table);
  return c;
}

/* ─── Transforms: row ↔ record ──────────────────────────────────────────── */

function snakePrefixOf(appField) {
  /* "supplierId" → "supplier" (strip trailing Id, then camel-to-snake). */
  const base = appField.endsWith('Id') ? appField.slice(0, -2) : appField;
  return base.replace(/([A-Z])/g, function (m) { return '_' + m.toLowerCase(); }).replace(/^_/, '');
}

/* Resolve a legacy id to its uuid using the in-memory cache. Returns
   null if the related table isn't loaded yet or no match is found —
   the save proceeds anyway with the uuid column left null, and the
   migration script's two-phase approach can clean it up later. */
function resolveUuidFromCache(table, legacyId) {
  if (legacyId === undefined || legacyId === null || legacyId === '') return null;
  const c = cache.get(table);
  if (!c) return null;
  const target = String(legacyId);
  const hit = c.records.find(function (r) { return String(r.id) === target || String(r._uuid) === target; });
  return hit ? hit._uuid : null;
}

/* Supabase row → app-shape record. */
function rowToRecord(table, row) {
  if (!row) return null;
  const cfg = configFor(table);
  /* Start from `extra` so the catch-all jsonb is spread underneath the
     typed columns, which then take precedence. */
  const out = (row.extra && typeof row.extra === 'object') ? { ...row.extra } : {};

  cfg.columns.forEach(function (col) {
    if (row[col.db] !== undefined && row[col.db] !== null) {
      out[col.app] = row[col.db];
    }
  });

  /* shipper_shipments: lift embarque-level upload slots back to the
     top of the record. recordToRow routes them THROUGH the promoted
     `docs` jsonb column on save (so they bypass stripBlobs); this
     lifts them back out so the admin UI keeps reading
     `emb.invoiceFile.data` exactly as it always did. Legacy rows
     that were saved with the slots at top-level (whitelist path) are
     left untouched — Object.assign style: only lift when the lifted
     field doesn't already exist on the record. */
  if (table === 'shipper_shipments' && out.docs && typeof out.docs === 'object') {
    ['invoiceFile', 'exportCertFile', 'otherDocs'].forEach(function (k) {
      if (out[k] === undefined && out.docs[k] !== undefined) out[k] = out.docs[k];
    });
    if (typeof console !== 'undefined' && console.log) {
      var dbg = {
        id: row.legacy_id,
        consec: row.consec_embarque,
        invoiceFile: summarizeFile(out.invoiceFile),
        exportCertFile: summarizeFile(out.exportCertFile),
        otherDocs: Array.isArray(out.otherDocs) ? out.otherDocs.length + ' file(s)' : '—',
        docsCol: row.docs ? Object.keys(row.docs).join(',') || '(empty)' : '(null)',
        extraKeys: row.extra ? Object.keys(row.extra).join(',') : '(none)',
      };
      console.log('[supabaseDB][read] shipper_shipments →', dbg);
    }
  }

  cfg.fks.forEach(function (fk) {
    const prefix = snakePrefixOf(fk.app);
    if (fk.noLegacyColumn) {
      /* No `_legacy_id` sibling. Read the uuid directly. */
      const dbCol = fk.dbUuid || (prefix + '_id');
      if (row[dbCol] !== undefined) out[fk.app] = row[dbCol];
    } else {
      const legacyCol = prefix + '_legacy_id';
      const uuidCol   = prefix + '_id';
      if (row[legacyCol] !== undefined && row[legacyCol] !== null) out[fk.app] = row[legacyCol];
      if (row[uuidCol]   !== undefined && row[uuidCol]   !== null) out['_' + fk.app + 'Uuid'] = row[uuidCol];
    }
  });

  /* ID handling. shipper_settings uses supplier_id as PK; the record's
     `id` becomes the supplier's legacy id. company_profile uses the
     constant 'gfi'. Everything else: legacy_id is the app id. */
  if (cfg.pk === 'supplier_id') {
    /* supplier_legacy_id was already mapped to record.supplierId
       above by the FK loop — promote it to `id` for app convenience. */
    out.id = row.supplier_legacy_id || row.supplier_id;
    out._uuid = row.supplier_id;
  } else if (cfg.singletonId) {
    out.id = cfg.singletonId;
  } else {
    out.id = row.legacy_id;
    out._uuid = row.id;
  }

  /* Always expose timestamps for debugging / "last modified" displays. */
  if (row.created_at) out._createdAt = row.created_at;
  if (row.updated_at) out._updatedAt = row.updated_at;
  return out;
}

/* Coerce empty string / undefined → null before sending to Postgres.
   Promoted columns are typed (numeric / date / timestamptz / text), and
   numeric/date columns REJECT empty string with "invalid input syntax".
   Text columns accept "" but null is semantically the same here and
   keeps the row builder column-type-agnostic. */
function coerceForColumn(value) {
  if (value === undefined) return null;
  if (value === '') return null;
  return value;
}

/* App-shape record → Supabase row. */
function recordToRow(table, record) {
  if (!record) return null;
  const cfg = configFor(table);
  const row = {};

  /* shipper_shipments: route embarque-level upload slots (invoiceFile,
     exportCertFile, otherDocs) THROUGH the promoted `docs` jsonb
     column instead of leaving them in `extra`. The promoted-column
     path bypasses stripBlobs entirely — which is what kept failing:
     even with the slots whitelisted, base64 PDF payloads still
     occasionally got dropped on the way to Supabase. Putting them in
     a typed jsonb column gives us a deterministic, stripBlobs-free
     channel for the file data. rowToRecord lifts them back to
     top-level on read so the consumer UI is unchanged. */
  if (table === 'shipper_shipments') {
    record = { ...record };
    var mergedDocs = Object.assign({}, record.docs || {});
    var routed = false;
    ['invoiceFile', 'exportCertFile', 'otherDocs'].forEach(function (k) {
      if (record[k] !== undefined && record[k] !== null) {
        mergedDocs[k] = record[k];
        delete record[k]; /* remove from top-level so the "everything
                             else → extra" loop below doesn't also
                             dump the same base64 payload into the
                             `extra` jsonb — that path runs stripBlobs
                             and double-stores the file. */
        routed = true;
      }
    });
    if (routed) record.docs = mergedDocs;
    if (typeof console !== 'undefined' && console.log) {
      console.log('[supabaseDB][write] shipper_shipments →', {
        id: record.id,
        consec: record.consecEmbarque,
        invoiceFile: summarizeFile(mergedDocs.invoiceFile),
        exportCertFile: summarizeFile(mergedDocs.exportCertFile),
        otherDocs: Array.isArray(mergedDocs.otherDocs) ? mergedDocs.otherDocs.length + ' file(s)' : '—',
        routedToDocsColumn: routed,
      });
    }
  }

  /* Promoted columns. */
  const promotedApp = new Set();
  cfg.columns.forEach(function (col) {
    promotedApp.add(col.app);
    if (record[col.app] !== undefined) row[col.db] = coerceForColumn(record[col.app]);
  });

  /* Foreign keys: write BOTH the legacy text + the uuid resolved from
     cache. The migration script's two-phase load already populates the
     cache for any related catalog before its dependents are saved. */
  const fkApp = new Set();
  cfg.fks.forEach(function (fk) {
    fkApp.add(fk.app);
    const legacyVal = record[fk.app];
    const prefix = snakePrefixOf(fk.app);
    if (fk.noLegacyColumn) {
      const dbCol = fk.dbUuid || (prefix + '_id');
      row[dbCol] = legacyVal ? resolveUuidFromCache(fk.table, legacyVal) : null;
    } else {
      row[prefix + '_legacy_id'] = legacyVal !== undefined && legacyVal !== null && legacyVal !== ''
        ? String(legacyVal) : null;
      row[prefix + '_id'] = legacyVal ? resolveUuidFromCache(fk.table, legacyVal) : null;
    }
  });

  /* PK column. */
  if (cfg.pk === 'supplier_id') {
    /* record.id is the supplier's legacy id. Resolve to uuid; if not in
       cache yet, we leave supplier_id null and the upsert will fail —
       caller must ensure suppliers are loaded first. */
    const supplierUuid = resolveUuidFromCache(cfg.pkResolveTable || 'suppliers', record.id);
    row.supplier_id = supplierUuid;
    row.supplier_legacy_id = record.id != null ? String(record.id) : null;
  } else if (cfg.singletonId) {
    row.id = cfg.singletonId;
  } else {
    row.legacy_id = record.id != null ? String(record.id) : null;
  }

  /* Everything else lands in `extra`. Skip transient app fields and the
     promoted/FK ones we already wrote. */
  const extra = {};
  for (const k of Object.keys(record)) {
    if (k === 'id' || k === '_uuid' || k === '_createdAt' || k === '_updatedAt') continue;
    if (k.startsWith('_')) continue; // hidden helpers (e.g. _supplierIdUuid)
    if (promotedApp.has(k)) continue;
    if (fkApp.has(k)) continue;
    extra[k] = record[k];
  }
  /* Pull whitelisted long-text fields out of the stripBlobs pass.
     stripBlobs was added to keep base64 PDFs / inline data URLs out
     of JSONB; for fields that LEGITIMATELY hold long HTML (email
     signatures with inline imagery, email body templates) we must
     bypass it or the user's content gets silently wiped on save. */
  const passthrough = {};
  for (const k of EXTRA_BLOB_WHITELIST) {
    if (extra[k] !== undefined) {
      passthrough[k] = extra[k];
      delete extra[k];
    }
  }
  row.extra = Object.assign({}, stripBlobs(extra), passthrough);
  return row;
}

/* Field names that are allowed to carry long strings inside `extra`
   without being nulled by stripBlobs. Add to this list any new field
   that holds user-authored HTML / long text the user expects to
   round-trip through Supabase verbatim. */
/* labelPhoto carries a base64 image data URL — stripBlobs would null
   it. Whitelisting keeps the user's uploaded label visible across
   browsers / sessions (FIX 63). The product's writeBinaries() still
   mirrors to localStorage for instant first paint, but Supabase is
   now the durable source of truth.

   invoiceFile / exportCertFile / otherDocs are embarque-level upload
   slots on `shipper_shipments`. They sit OUTSIDE the promoted column
   map (only `proformas` / `docs` / `notes` are typed columns), so
   without whitelisting they fall into `extra` and stripBlobs nulls
   their `.data` base64 payload — the admin then reads the embarque
   back with broken file links and the review modal renders "Sin
   factura del shipper" / "No shipper invoice file" even though the
   shipper successfully uploaded the PDF. Whitelisting routes the
   whole file object (name / size / type / data) around stripBlobs.
   Proforma-level uploads (palletPhotos, the proforma's own otherDocs)
   already survive because they live inside the promoted `proformas`
   jsonb column which bypasses stripBlobs entirely. */
/* Note: `labelPhoto` was previously on this list, which let base64
   image data URLs leak into `products.extra` and bloat the row by
   100s of KB per product. The image now lives ONLY in the
   `product-labels` Storage bucket, addressed via labelPhotoPath. If
   the upload fails on save, the row is written with labelPhoto=null
   (no fallback) — better to lose the visual than to grow the row
   into the multi-MB range that breaks the shipments list load. */
const EXTRA_BLOB_WHITELIST = ['emailSignatures', 'emailTemplates', 'invoiceFile', 'exportCertFile', 'otherDocs'];

/* Compact one-line summary of a shipper-portal file metadata object,
   used by the shipper_shipments diagnostic logs. Reports which of
   the three possible payload sources is present:
     - `url`  → Storage public URL (new path, post-shipment-docs bucket)
     - `path` → Storage object key
     - `data` → legacy base64 data URL (pre-bucket records)
   Bytes are counted but never logged verbatim — a 5MB PDF would
   flood devtools. */
function summarizeFile(f) {
  if (!f) return '(none)';
  if (typeof f !== 'object') return String(f);
  var bits = [f.name || '(unnamed)', f.size || '?'];
  if (typeof f.url === 'string' && f.url) bits.push('url=' + f.url.length + 'ch');
  if (typeof f.path === 'string' && f.path) bits.push('path=' + f.path);
  if (typeof f.data === 'string') bits.push('data=' + f.data.length + 'ch');
  else if (f.data != null) bits.push('data=non-string');
  return bits.join(' · ');
}

/* Mirror of supabaseMigration.js stripBlobs — keep them in sync. Stops
   inline base64/PDF payloads from being written to JSONB (those go to
   Supabase Storage in a separate step). */
const BLOB_THRESHOLD = 2000;
function stripBlobs(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (value.startsWith('data:')) return null;
    if (value.length > BLOB_THRESHOLD) return null;
    return value;
  }
  if (Array.isArray(value)) return value.map(stripBlobs);
  if (typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = stripBlobs(value[k]);
    return out;
  }
  return value;
}

/* ─── Public API ────────────────────────────────────────────────────────── */

/* Load all rows from `table` and return them as app-shape records.
   `opts.force` bypasses the cache. `opts.ttlMs` overrides the default
   freshness window. */
export async function loadFromSupabase(table, opts) {
  const force = opts && opts.force === true;
  const ttl   = opts && typeof opts.ttlMs === 'number' ? opts.ttlMs : DEFAULT_TTL_MS;

  const cached = cache.get(table);
  if (!force && cached && (Date.now() - cached.fetchedAt) < ttl) {
    return cached.records;
  }

  /* PostgREST caps a single .select('*') response at 1000 rows by
     default — silently, no error, no truncation flag. Every table in
     this app stayed under that cap until historical_prices' 2026-09-15
     7-year USDA/SNIIM backfill pushed it past 6,000 rows, at which point
     FOB Prices/Forecast started reading a truncated ~1000-row slice
     with no indication anything was missing. Paginate with .range() in
     1000-row pages until a page comes back short, so any table (this
     one today, potentially others later) loads completely regardless
     of size. */
  const PAGE_SIZE = 1000;
  var allRows = [];
  var offset = 0;
  for (;;) {
    const { data, error } = await supabase.from(table).select('*').range(offset, offset + PAGE_SIZE - 1);
    if (error) throw new Error('[supabaseDB] load ' + table + ': ' + error.message);
    allRows = allRows.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  const records = allRows.map(function (row) { return rowToRecord(table, row); });
  cache.set(table, { records: records, fetchedAt: Date.now() });
  notify(table);
  return records;
}

/* Insert or update one record. Returns the saved record (with `_uuid`
   and timestamps from Postgres). Throws on error.

   Important: this writes Supabase first (source of truth), then patches
   the cache locally with the returned row — so a successful save is
   immediately visible to subscribers without a refetch. If the save
   fails, the cache is left untouched. */
export async function saveToSupabase(table, record) {
  const cfg = configFor(table);
  const row = recordToRow(table, record);
  const conflict = cfg.pk || 'legacy_id';

  const { data, error } = await supabase
    .from(table)
    .upsert([row], { onConflict: conflict })
    .select('*');
  if (error) throw new Error('[supabaseDB] save ' + table + ': ' + error.message);

  const saved = (data && data[0]) ? rowToRecord(table, data[0]) : record;

  /* Patch cache: replace by app id if found, else push. */
  const cached = cache.get(table);
  if (cached) {
    const idx = cached.records.findIndex(function (r) { return String(r.id) === String(saved.id); });
    if (idx >= 0) cached.records[idx] = saved;
    else cached.records = cached.records.concat([saved]);
    cached.fetchedAt = Date.now();
  }
  notify(table);
  return saved;
}

/* Bulk variant — same shape, but takes an array. Single round-trip.
   Returns the array of saved records. */
export async function saveManyToSupabase(table, records) {
  if (!records || records.length === 0) return [];
  const cfg = configFor(table);
  const rows = records.map(function (r) { return recordToRow(table, r); });
  const conflict = cfg.pk || 'legacy_id';

  const { data, error } = await supabase
    .from(table)
    .upsert(rows, { onConflict: conflict })
    .select('*');
  if (error) throw new Error('[supabaseDB] saveMany ' + table + ': ' + error.message);

  const saved = (data || []).map(function (row) { return rowToRecord(table, row); });
  /* Patch cache by id, same as saveToSupabase — NEVER replace wholesale.
     This table's bulk save is almost always a PARTIAL subset (e.g.
     ShipmentRegistry's handleSave re-stamping just the 1-2 bills tied
     to a lot, or PaymentRegistry re-stamping a handful of allocations)
     — replacing cache.records with only `saved` silently truncated
     every OTHER already-cached record for this table. Any later
     getCached(table) or loadFromSupabase(table) (without force, inside
     the TTL window) then saw that truncated list — e.g. a Bill's
     "Linked Invoices" (findInvoicesByLot over getCached("invoices"))
     going blank, or an entire list screen losing rows, right after an
     unrelated partial bulk save elsewhere touched the same table.
     Real production bug, 2026-10-14 (Arturo: shipment transport data
     "se borran" and a just-created invoice not showing as linked to
     its Bill — traced to this wholesale cache replace). */
  const cached = cache.get(table);
  if (cached) {
    const byId = {};
    saved.forEach(function (r) { byId[String(r.id)] = r; });
    const next = cached.records.map(function (r) { return byId[String(r.id)] || r; });
    saved.forEach(function (r) {
      if (!next.some(function (r2) { return String(r2.id) === String(r.id); })) next.push(r);
    });
    cached.records = next;
    cached.fetchedAt = Date.now();
  } else {
    cache.set(table, { records: saved, fetchedAt: Date.now() });
  }
  notify(table);
  return saved;
}

/* Delete one record by id. The id is the app-side identifier (legacy
   string for most tables, supplier legacy id for shipper_settings,
   'gfi' for company_profile). */
export async function deleteFromSupabase(table, id) {
  if (id === undefined || id === null || id === '') {
    throw new Error('[supabaseDB] delete ' + table + ': id required');
  }
  const cfg = configFor(table);
  let q = supabase.from(table).delete();

  if (cfg.pk === 'supplier_id') {
    const uuid = resolveUuidFromCache(cfg.pkResolveTable || 'suppliers', id);
    if (!uuid) {
      throw new Error('[supabaseDB] delete shipper_settings: supplier uuid not in cache for ' + id);
    }
    q = q.eq('supplier_id', uuid);
  } else if (cfg.singletonId) {
    q = q.eq('id', cfg.singletonId);
  } else {
    q = q.eq('legacy_id', String(id));
  }

  const { error } = await q;
  if (error) throw new Error('[supabaseDB] delete ' + table + ': ' + error.message);

  /* Drop from cache. */
  const cached = cache.get(table);
  if (cached) {
    cached.records = cached.records.filter(function (r) { return String(r.id) !== String(id); });
    cached.fetchedAt = Date.now();
  }
  notify(table);
}

/* ─── Cache helpers ─────────────────────────────────────────────────────── */

/* Drop the cache for one table (or all tables if `table` is omitted) so
   the next load fetches fresh from Supabase. Doesn't notify — callers
   typically re-load right after invalidating. */
export function invalidateCache(table) {
  if (table) cache.delete(table);
  else cache.clear();
}

/* Synchronous accessor — returns whatever's in cache without touching
   the network. Returns an empty array if the table hasn't been loaded
   yet. Useful for render-time reads after an async load already ran. */
export function getCached(table) {
  const c = cache.get(table);
  return c ? c.records : [];
}

/* Subscribe to changes on a table. Callback fires after every load,
   save, saveMany, and delete on this table. Returns an unsubscribe fn. */
export function onChange(table, callback) {
  if (typeof callback !== 'function') return function () {};
  if (!subs.has(table)) subs.set(table, new Set());
  subs.get(table).add(callback);
  return function () {
    const set = subs.get(table);
    if (set) set.delete(callback);
  };
}

/* ─── Convenience: prime catalog caches in dependency order ─────────────── */
/* Useful at app boot. Loads catalogs first so save paths can resolve
   FKs from in-memory cache without an extra round-trip. Operations
   tables are loaded lazily by the modules that need them. */
export async function primeCatalogs() {
  const tables = ['seasons', 'suppliers', 'clients', 'products', 'warehouses', 'brokers', 'export_certs', 'fee_catalog', 'company_profile', 'commodities'];
  await Promise.all(tables.map(function (t) {
    return loadFromSupabase(t).catch(function (e) {
      // eslint-disable-next-line no-console
      console.warn('[supabaseDB] primeCatalogs failed for ' + t + ':', e.message);
      return [];
    });
  }));
}

/* Default TTL exported for tests / experimentation. */
export const CACHE_TTL_MS = DEFAULT_TTL_MS;
